// Module 4.1a — Kill Sheet Hub (Operational Engineering layer).
//
// Lists every kill sheet owned by a single well (scoped via wellId).
// Reached from `/wells/:wellId/kill-sheets`.
//
// This module sits ABOVE the older /kill-sheet/:variant wizard. It uses the
// new operational-engineering core (`lib/openg`) and stores via
// `lib/killsheet/storage`. The legacy wizard remains for the printable IWCF
// flow until 4.1b folds presentation into this stack.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, ArrowLeft, Trash2, Gauge, Lock } from "lucide-react";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentClasses, stackAccentColor } from "@/lib/stackAccent";
import {
  listKillSheetsForWell, deleteKillSheet, getUnreadableKillSheetCount,
} from "@/lib/killsheet/storage";
import { isProActive } from "@/lib/featureAccess";
import { getCachedEntitlement } from "@/lib/entitlement";

const STATUS_COLOR = {
  draft:      "border-zinc-500/40 text-zinc-300",
  active:     "border-sky-500/40 text-sky-300",
  locked:     "border-amber-500/40 text-amber-400",
  used:       "border-emerald-500/40 text-emerald-300",
  superseded: "border-zinc-600/40 text-zinc-400",
  archived:   "border-zinc-700/40 text-zinc-500",
};

function StatusPill({ status, testId }) {
  const cls = STATUS_COLOR[status] || "border-zinc-500/40 text-zinc-400";
  return (
    <span data-testid={testId}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-[0.16em] ${cls}`}>
      {status === "locked" && <Lock className="w-2.5 h-2.5" aria-hidden="true" />}
      {status}
    </span>
  );
}

export default function KillSheetOpHub() {
  const { stackMode } = useStackMode();
  const accent = stackAccentClasses(stackMode);
  const navigate = useNavigate();
  const { wellId } = useParams();
  const [well, setWell] = useState(() => getWellProfile(wellId));
  const [rows, setRows] = useState(() => listKillSheetsForWell(wellId));
  const [unreadableCount, setUnreadableCount] = useState(() => getUnreadableKillSheetCount());
  const [q, setQ] = useState("");

  const refresh = () => {
    setWell(getWellProfile(wellId));
    setRows(listKillSheetsForWell(wellId));
    setUnreadableCount(getUnreadableKillSheetCount());
  };

  useEffect(() => {
    const onFocus = () => {
      setWell(getWellProfile(wellId));
      setRows(listKillSheetsForWell(wellId));
      setUnreadableCount(getUnreadableKillSheetCount());
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [wellId]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      [r.name, r.sheetNumber, r.revision, r.status, r.basis?.killMethod,
       r.basis?.wellControlStandard, r.preparedBy]
        .filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [rows, q]);

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — this hub is reached by a URL that pins a
  // specific wellId. Flipping the global operating-mode toggle while such a
  // URL stays open must not silently keep rendering the other mode's well
  // underneath the new mode's chrome/accent — that is exactly the
  // cross-contamination this correction exists to prevent.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its kill sheets, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete kill sheet "${name || "(unnamed)"}"?`))
      return;
    deleteKillSheet(id);
    refresh();
  };

  return (
    <div data-testid="ks-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <Link to={`/wells/${wellId}`}
              data-testid="ks-hub-back"
              className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> {well.wellName || "Well"}
        </Link>
        <Link to={`/wells/${wellId}/kill-sheets/new`}
              data-testid="ks-hub-new"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
              style={{ background: stackAccentColor(stackMode), color: "#0A0B0E" }}>
          <Plus className="w-3.5 h-3.5" /> New Kill Sheet
        </Link>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 4.1a · Operational Engineering
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          Kill Sheets
        </h1>
        <p className="text-xs text-[var(--rs-text-dim,#9aa6b2)] mt-1 leading-relaxed">
          Deterministic kill calculations (KMW, ICP, FCP, strokes, times)
          backed by frozen audit snapshots. The full Driller&apos;s Schedule and
          print layouts ship in Module 4.1b.
        </p>
      </div>

      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter — name, sheet #, status, standard…"
        data-testid="ks-hub-search"
        className="input-rs"
      />

      {unreadableCount > 0 && (
        <div data-testid="ks-hub-unreadable-notice"
             className="card-rs p-3 text-xs text-amber-300 border-amber-500/30">
          {unreadableCount} kill sheet{unreadableCount === 1 ? "" : "s"} could not be read —
          created by an older version of RigSentinel. They are not shown here and were not
          modified or deleted.
        </div>
      )}

      {filtered.length === 0 ? (
        <div data-testid="ks-hub-empty"
             className="card-rs p-6 text-center text-sm text-[var(--rs-text-dim,#9aa6b2)]">
          {rows.length === 0
            ? <>No kill sheets yet for this well. Tap <span className={`font-medium ${accent.text}`}>+ New Kill Sheet</span> to start.</>
            : <>No kill sheets match the current filter.</>}
        </div>
      ) : (
        <div data-testid="ks-hub-list" className="flex flex-col gap-2">
          {filtered.map((r) => (
            <div key={r.id}
                 data-testid={`ks-row-${r.id}`}
                 className="card-rs p-3 flex items-start gap-3">
              <Link to={`/wells/${wellId}/kill-sheets/${r.id}`}
                    data-testid={`ks-open-${r.id}`}
                    className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Gauge className={`w-3.5 h-3.5 shrink-0 ${accent.text}`} aria-hidden="true" />
                  <span className="font-semibold text-sm text-[var(--rs-text,#e8edf2)] truncate">
                    {r.name || "(unnamed)"}
                  </span>
                  {r.sheetNumber && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-zinc-500/40 text-zinc-300">
                      #{r.sheetNumber}{r.revision ? ` · ${r.revision}` : ""}
                    </span>
                  )}
                  <StatusPill status={r.status} testId={`ks-row-${r.id}-status`} />
                  {r.basis?.wellControlStandard && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300">
                      {r.basis.wellControlStandard}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-[var(--rs-text-dim,#9aa6b2)] flex items-center gap-2 flex-wrap">
                  {r.basis?.killMethod && <span>{r.basis.killMethod}</span>}
                  {r.basis?.fluidBasis && <span>· {r.basis.fluidBasis}</span>}
                  {r.basis?.influxType && <span>· influx: {r.basis.influxType}</span>}
                  {isProActive(getCachedEntitlement()) && r.outputs?.kmwPpg !== "" && r.outputs?.kmwPpg != null && (
                    <span>· KMW {Number(r.outputs.kmwPpg).toFixed(2)} ppg</span>
                  )}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
                  Updated {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : "—"}
                  {r.pullSnapshot?.pulledAt && (
                    <> · pulled {new Date(r.pullSnapshot.pulledAt).toLocaleDateString()}</>
                  )}
                </div>
              </Link>
              <button type="button"
                      onClick={() => handleDelete(r.id, r.name)}
                      data-testid={`ks-delete-${r.id}`}
                      aria-label={`Delete ${r.name}`}
                      className="shrink-0 p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
